const keyCode = Object.freeze({
    ESCAPE: 'Escape'
});

/**
 * @description Base ShowMore implementation
 * @param Widget Base widget for extending
 * @returns ShowMore class
 */
function ShowMoreClassCreator(AccessibilityFocusTrapMixin: import('widgets/global/AccessibilityFocusTrapMixin').TAccessibilityFocusTrapMixin) {
    /**
     * @category widgets
     * @subcategory global
     * @class ShowMore
     * @augments Widget
     * @property {string} data-widget - Widget name `showMore`
     * @example <caption>Typical ShowMore widget markup</caption>
     * <div
     *     class="showmore"
     *     data-widget="showMore"
     *     data-id="showMore"
     * >
     *     <div class="content-visible">
     *         ... content
     *     </div>
     *
     *     <button
     *         class="showmore-button"
     *         type="button"
     *         data-event-click.prevent="showContent"
     *         data-ref="showMoreButton"
     *     >
     *         Show more
     *     </button>
     *
     *     <div
     *         class="content-hidden"
     *         data-ref="showMoreContainer"
     *         hidden="hidden"
     *     >
     *         <div class="content-inner"
     *             ... content
     *         </div>
     *
     *         <button
     *             class="showless-button"
     *             type="button"
     *             data-event-click.prevent="hideContent"
     *             data-ref="showLessButton"
     *         >
     *             Show less
     *         </button>
     *     </div>
     * </div>
     */
    class ShowMore extends AccessibilityFocusTrapMixin {
        backFocusElement?: HTMLElement | null;

        escHandler?: () => void;

        clickOutsideHandler?: () => void;

        prefs() {
            return {
                ...super.prefs(),
                classesShow: 'm-opened',
                classesActive: 'm-active',
                refContainer: 'showDrawerContainer'
            };
        }

        init() {
            super.init();

            this.addFocusTraps();
            this.hideContent();
            this.changeInputLabel();
        }

        /**
         * @description Add Click Outside / Close by ESC Listener
         */
        addListeners() {
            this.clickOutsideHandler = this.ev(
                'click',
                (_, event) => {
                    if (event.target === this.ref(this.prefs().refContainer).get()) {
                        this.hideDrawer();
                    }
                },
                this.ref(this.prefs().refContainer).get()
            ).pop();

            this.escHandler = this.ev(
                'keyup',
                (_, event) => {
                    const keyboardEvent = <KeyboardEvent>event;

                    if (keyboardEvent.key === keyCode.ESCAPE) {
                        this.hideDrawer();
                    }
                },
                window
            ).pop();
        }

        /**
         * @description Clean Up Listeners
         */
        cleanUpListeners() {
            if (this.escHandler) {
                this.escHandler();
                this.escHandler = undefined;
            }

            if (this.clickOutsideHandler) {
                this.clickOutsideHandler();
                this.clickOutsideHandler = undefined;
            }
        }

        /**
         * @description Show content
         */
        showContent() {
            this.ref('showMoreContainer').show();
            this.ref('showMoreButton').hide();
            this.ref('showLessButton').show();
            this.ref('self').addClass(this.prefs().classesShow);
        }

        /**
         * @description Hide content
         */
        hideContent() {
            this.ref('showMoreContainer').hide();
            this.ref('showMoreButton').show();
            this.ref('showLessButton').hide();
            this.ref('self').removeClass(this.prefs().classesShow);
        }

        /**
         * @description Show drawer
         */
        showDrawer() {
            this.ref('showDrawerContainer').addClass(this.prefs().classesShow);
            this.ref('showDrawerDialog').addClass(this.prefs().classesActive);
            this.backFocusElement = <HTMLElement>document.activeElement;
            this.focusFirstElement();
            this.addListeners();
        }

        /**
         * @description Hide drawer
         */
        hideDrawer() {
            this.ref('showDrawerContainer').removeClass(this.prefs().classesShow);
            this.ref('showDrawerDialog').removeClass(this.prefs().classesActive);

            this.cleanUpListeners();

            if (this.backFocusElement) {
                this.backFocusElement.focus();
                this.backFocusElement = null;
            }
        }

        /**
         * @description Sets aria-label on input checkbox for labels that are not ADA-compliant
         */
        changeInputLabel() {
            const agreeToPrivacyElement = this.ref('self').get()?.closest('[data-widget="inputCheckbox"]');

            if (agreeToPrivacyElement) {
                const contentVisible = this.ref('self').get()?.querySelector('.content-visible')?.textContent;

                if (contentVisible) {
                    agreeToPrivacyElement.querySelector('input[data-change-input-label="true"]')?.setAttribute('aria-label', contentVisible);
                }
            }
        }
    }

    return ShowMore;
}

export type TShowMore = ReturnType<typeof ShowMoreClassCreator>;

export type TShowMoreInstance = InstanceType<TShowMore>;

export default ShowMoreClassCreator;
